import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Mail,
  User,
  Clock,
  Send,
  Loader2,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  Trash2,
  Eye,
  Filter,
  RefreshCw,
  Inbox,
  Search
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  new: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  in_progress: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  resolved: 'bg-green-500/20 text-green-400 border-green-500/30',
  closed: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};

const STATUS_LABELS = {
  new: 'New',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed'
};

export default function ContactInquiriesManager() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({ total: 0, unread: 0, new_count: 0 });
  const [inquiryTypes, setInquiryTypes] = useState({});

  useEffect(() => {
    fetchInquiries();
  }, [statusFilter, typeFilter]);

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status_filter', statusFilter);
      if (typeFilter && typeFilter !== 'all') params.append('type_filter', typeFilter);
      
      const response = await axios.get(`${API}/api/public-contact/inquiries?${params.toString()}`);
      setInquiries(response.data.inquiries || []);
      setStats({
        total: response.data.total || 0,
        unread: response.data.unread || 0,
        new_count: response.data.new_count || 0
      });
      setInquiryTypes(response.data.inquiry_types || {});
    } catch (error) {
      console.error('Failed to fetch inquiries:', error);
      toast.error('Failed to load contact inquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleViewInquiry = async (inquiry) => {
    setSelectedInquiry(inquiry);
    setResponseText('');
    
    // Mark as read
    if (!inquiry.is_read) {
      try {
        await axios.get(`${API}/api/public-contact/inquiries/${inquiry.id}`);
        fetchInquiries();
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  };

  const handleSendResponse = async () => {
    if (!responseText.trim() || !selectedInquiry) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/api/public-contact/inquiries/${selectedInquiry.id}/respond`, {
        response_message: responseText.trim()
      });
      
      toast.success('Response sent successfully');
      setResponseText('');
      fetchInquiries();
      
      // Refresh selected inquiry
      const response = await axios.get(`${API}/api/public-contact/inquiries/${selectedInquiry.id}`);
      setSelectedInquiry(response.data);
    } catch (error) {
      toast.error('Failed to send response');
    } finally {
      setSending(false);
    }
  };

  const handleUpdateStatus = async (inquiryId, newStatus) => {
    try {
      await axios.put(`${API}/api/public-contact/inquiries/${inquiryId}/status?new_status=${newStatus}`);
      toast.success(`Status updated to ${STATUS_LABELS[newStatus]}`);
      fetchInquiries();
      
      if (selectedInquiry?.id === inquiryId) {
        setSelectedInquiry(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteInquiry = async (inquiryId) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) return;
    
    try {
      await axios.delete(`${API}/api/public-contact/inquiries/${inquiryId}`);
      toast.success('Inquiry deleted');
      fetchInquiries();
      
      if (selectedInquiry?.id === inquiryId) {
        setSelectedInquiry(null);
      }
    } catch (error) {
      toast.error('Failed to delete inquiry');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredInquiries = inquiries.filter(inquiry => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      inquiry.full_name?.toLowerCase().includes(query) ||
      inquiry.email?.toLowerCase().includes(query) ||
      inquiry.message?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[#252540] border-[#2d2d44]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-blue-500/20">
              <Inbox className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Total Inquiries</p>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#252540] border-[#2d2d44]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-yellow-500/20">
              <Mail className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Unread</p>
              <p className="text-2xl font-bold text-white">{stats.unread}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#252540] border-[#2d2d44]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-green-500/20">
              <AlertCircle className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">New</p>
              <p className="text-2xl font-bold text-white">{stats.new_count}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px] bg-[#252540] border-[#2d2d44] text-white">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#252540] border-[#2d2d44]">
              <SelectItem value="all" className="text-white">All Status</SelectItem>
              <SelectItem value="new" className="text-white">New</SelectItem>
              <SelectItem value="in_progress" className="text-white">In Progress</SelectItem>
              <SelectItem value="resolved" className="text-white">Resolved</SelectItem>
              <SelectItem value="closed" className="text-white">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px] bg-[#252540] border-[#2d2d44] text-white">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent className="bg-[#252540] border-[#2d2d44]">
            <SelectItem value="all" className="text-white">All Types</SelectItem>
            {Object.entries(inquiryTypes).map(([value, label]) => (
              <SelectItem key={value} value={value} className="text-white">{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Search inquiries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#252540] border-[#2d2d44] text-white"
            />
          </div>
        </div>
        
        <Button
          variant="outline"
          onClick={fetchInquiries}
          className="border-[#2d2d44] text-gray-400 hover:text-white"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Inquiries List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* List */}
        <Card className="bg-[#1a1a2e] border-[#2d2d44]">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-lg">Inquiries</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#6D4AFF]" />
              </div>
            ) : filteredInquiries.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Inbox className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No inquiries found</p>
              </div>
            ) : (
              <ScrollArea className="h-[500px]">
                <div className="space-y-2 pr-4">
                  {filteredInquiries.map((inquiry) => (
                    <div
                      key={inquiry.id}
                      onClick={() => handleViewInquiry(inquiry)}
                      className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                        selectedInquiry?.id === inquiry.id
                          ? 'bg-[#6D4AFF]/20 border-[#6D4AFF]'
                          : 'bg-[#252540] border-[#2d2d44] hover:border-[#6D4AFF]/50'
                      } ${!inquiry.is_read ? 'border-l-4 border-l-blue-500' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white truncate">{inquiry.full_name}</p>
                          <p className="text-sm text-gray-400 truncate">{inquiry.email}</p>
                        </div>
                        <Badge className={STATUS_COLORS[inquiry.status]}>
                          {STATUS_LABELS[inquiry.status]}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">
                        {inquiry.inquiry_type_label}
                        {inquiry.other_type && ` - ${inquiry.other_type}`}
                      </p>
                      <p className="text-sm text-gray-400 line-clamp-2">{inquiry.message}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        {formatDate(inquiry.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Detail View */}
        <Card className="bg-[#1a1a2e] border-[#2d2d44]">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-lg">
              {selectedInquiry ? 'Inquiry Details' : 'Select an Inquiry'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedInquiry ? (
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <User className="w-4 h-4" />
                      {selectedInquiry.full_name}
                    </h3>
                    <p className="text-sm text-[#6D4AFF]">{selectedInquiry.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select
                      value={selectedInquiry.status}
                      onValueChange={(value) => handleUpdateStatus(selectedInquiry.id, value)}
                    >
                      <SelectTrigger className="w-[130px] bg-[#252540] border-[#2d2d44] text-white h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#252540] border-[#2d2d44]">
                        <SelectItem value="new" className="text-white">New</SelectItem>
                        <SelectItem value="in_progress" className="text-white">In Progress</SelectItem>
                        <SelectItem value="resolved" className="text-white">Resolved</SelectItem>
                        <SelectItem value="closed" className="text-white">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteInquiry(selectedInquiry.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Inquiry Type & Date */}
                <div className="flex flex-wrap gap-2 text-sm">
                  <Badge variant="outline" className="border-[#6D4AFF] text-[#6D4AFF]">
                    {selectedInquiry.inquiry_type_label}
                    {selectedInquiry.other_type && `: ${selectedInquiry.other_type}`}
                  </Badge>
                  <span className="text-gray-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(selectedInquiry.created_at)}
                  </span>
                </div>

                {/* Message */}
                <div className="p-4 bg-[#252540] rounded-lg">
                  <p className="text-sm text-gray-400 mb-2 font-medium">Message:</p>
                  <p className="text-white whitespace-pre-wrap">{selectedInquiry.message}</p>
                </div>

                {/* Responses */}
                {selectedInquiry.responses?.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-400 font-medium">Responses:</p>
                    {selectedInquiry.responses.map((resp, idx) => (
                      <div key={idx} className="p-3 bg-[#6D4AFF]/10 rounded-lg border border-[#6D4AFF]/30">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-[#6D4AFF] font-medium">
                            {resp.responder_name || resp.responder_email}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(resp.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-300 whitespace-pre-wrap">{resp.message}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reply Box */}
                <div className="space-y-3 pt-4 border-t border-[#2d2d44]">
                  <Textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    placeholder="Type your response..."
                    className="bg-[#252540] border-[#2d2d44] text-white min-h-[100px]"
                  />
                  <Button
                    onClick={handleSendResponse}
                    disabled={!responseText.trim() || sending}
                    className="w-full bg-[#6D4AFF] hover:bg-[#5B3FD9]"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Response
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Select an inquiry to view details</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
